import useFetch from "hooks/useFetch";
import {
	Fragment,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import Icon from "../shared/Icon";
import appConfig from "config.json";
import {
	focusChatModal,
	isCurrentPathPresent,
	mapString,
	toggleBodyClass,
} from "utils";
import Button from "components/shared/Button";
import { Context } from "Context";
import useRouteChange from "hooks/useRouteChange";
import useCheckMediaQuery from "hooks/useCheckMediaQuery";
import Link from "components/shared/Link";
import { buildHref } from "utils/link";
import { getSurveyUrl } from "utils/surveyParams";

const { content, chatFloatingButtonId, mobileSmallMediaQuery } = appConfig;
const cacheMinutes = 60;
const animationDuration = 400;
const animationStates = {
	idle: "idle",
	in: "in",
	out: "out",
	hidden: "hidden",
	visible: "visible",
};
const cssKeys = [
	"availableIndicatorColor",
	"itemsBackground",
	"itemsBackgroundFocused",
	"itemsBackgroundHover",
	"itemsBackgroundPressed",
];
const mainClass = "syndicated-floating-help-hub";
const chatButtonId = "chat-floating-invite";
const defaultProperties = {
	titleAlign: "center",
	subtitleAlign: "center",
	buttonLabelType: "icon-title",
};
const itemClasses = `${mainClass}__popup__items__item`;
const previousElement = {
	reference: null,
};

const iconsColors = {
	default: "1e3c7d",
	interactive: "00233c",
};

const ItemContent = ({
	item = {},
	title = "",
	subtitle = "",
	indicator = false,
}) => (
	<>
		<p className={`${itemClasses}__link__information`}>
			{title || item.link.title}
			{subtitle ? (
				<span className={`${itemClasses}__link__information__subtitle`}>
					{indicator ? (
						<span className={`${itemClasses}__link__information__indicator`} />
					) : null}
					{subtitle}
				</span>
			) : null}
		</p>
		<Icon
			icon={item.icon}
			color={iconsColors.default}
			className={`${itemClasses}__link__icon--default ${itemClasses}__link__icon--${item?.id}`}
		/>
		<Icon
			icon={item.icon}
			color={iconsColors.interactive}
			className={`${itemClasses}__link__icon--interactive ${itemClasses}__link__icon--${item?.id}`}
		/>
	</>
);

const removeLegacyChatButtonFocus = () => {
	document.getElementById("lpChatButton")?.removeAttribute("tabindex");
};

const FloatingHelpHub = () => {
	const {
		config: { store, locale },
		chat: { chatFloatingButton, chatWindow, chatModal },
		setChat,
		ariaLiveRef,
		profileData,
	} = useContext(Context);

	const isMobileView = useCheckMediaQuery(mobileSmallMediaQuery);

	const [floatingHelpHubUrl, setFloatingHelpHubUrl] = useState("");
	const [isShowPopup, setIsShowPopup] = useState(false);
	const [animationState, setAnimationState] = useState(animationStates.idle);
	const [isEnabled, setIsEnabled] = useState(false);
	const [currentStates, setCurrentStates] = useState({
		isChatInvite: false,
		shouldCloseAfterChatInvite: false,
	});
	const [currentPath, setCurrentPath] = useState(window.location.pathname);
	const [feedbackUrl, setFeedbackUrl] = useState("");
	const dialogRef = useRef(null);
	const showButtonRef = useRef(null);

	const { response } = useFetch(
		floatingHelpHubUrl,
		{},
		cacheMinutes,
		floatingHelpHubUrl
	);

	const flexDirection = useMemo(
		() =>
			response?.properties?.itemsIconsPosition === "left"
				? "row-reverse"
				: "row",
		[response?.properties]
	);

	const handleRouteChange = (path) => setCurrentPath(path);
	useRouteChange(handleRouteChange);

	const removeFocusModalListener = () => {
		document.removeEventListener("keydown", addFocusModalEvent);
	};

	const addFocusModalEvent = (event) => {
		focusChatModal(
			event,
			dialogRef?.current,
			removeFocusModalListener,
			previousElement
		);
	};

	const handleKeydown = (e) => {
		if (e.key === "Escape") handleClosePopup();
	};

	const keysToCssProperties = () => {
		const cssProperties = {};
		cssKeys.forEach((cssKey) => {
			cssProperties[`--fhh-${cssKey}`] = response?.properties[cssKey];
		});
		return cssProperties;
	};

	const setAnalyticsEvent = (analyticsEvent) => {
		setChat((currentChat) => ({ ...currentChat, analyticsEvent }));
	};

	const interactWithChatInvite = (reject = true) => {
		const interactionElement = document.getElementById(
			reject ? "inviteReject" : "inviteAccept"
		);
		if (
			interactionElement &&
			chatModal &&
			chatModal.contains(interactionElement)
		) {
			interactionElement.click();
			removeFocusModalListener();
		}
	};

	const handleClosePopup = () => {
		document.removeEventListener("keydown", handleKeydown);
		setAnimationState(animationStates.out);
		setTimeout(() => {
			setAnimationState(animationStates.hidden);
			setIsShowPopup(false);
			if (currentStates.isChatInvite) {
				interactWithChatInvite();
				setCurrentStates({
					isChatInvite: false,
					shouldCloseAfterChatInvite: false,
				});
				previousElement.reference?.focus();
			} else {
				showButtonRef?.current?.focus();
			}
			toggleBodyClass(false);
			setFeedbackUrl("");
			ariaLiveRef.current.textContent = "";
		}, animationDuration);
	};

	const handleShowPopup = (preventFocus = false) => {
		if (!chatWindow) {
			setIsShowPopup(true);
			setAnimationState(animationStates.in);
			setTimeout(() => {
				setAnimationState(animationStates.visible);
				if (!preventFocus) dialogRef.current?.focus();
			}, animationDuration);
			document.addEventListener("keydown", handleKeydown);
		}
	};

	const interactWithChat = () => {
		document.getElementById(chatFloatingButtonId)?.click();
		setAnalyticsEvent("fhhChat");
	};

	const interactWithProactiveChat = () => {
		interactWithChatInvite(false);
	};

	const renderChatElement = (
		item,
		chatStatus,
		interact,
		indicator = false,
		elementId = null
	) => (
		<Button
			className={`${itemClasses}__link`}
			style={{ flexDirection }}
			elementId={elementId}
			onClick={interact}
			onKeyDown={(event) => {
				if (event?.key === "Enter") interact();
			}}
		>
			<ItemContent
				item={item}
				subtitle={chatStatus?.subtitle}
				title={chatStatus?.title}
				indicator={indicator}
			/>
		</Button>
	);

	const renderChatButton = (chatStatus, statusKey, item = {}) => {
		return (
			<li
				key={chatButtonId}
				className={`${itemClasses} ${itemClasses}--${locale} ${itemClasses}--chat${
					!chatFloatingButton || currentStates.isChatInvite ? "--hidden" : ""
				}${statusKey ? ` ${itemClasses}--chat--${statusKey}` : ""}`}
			>
				{renderChatElement(
					item,
					chatStatus,
					interactWithChat,
					false,
					chatButtonId
				)}
			</li>
		);
	};

	const renderProactiveChatButton = (chatStatus, item) => {
		if (currentStates.isChatInvite) {
			return (
				<li
					key={`${chatButtonId}-proactive`}
					className={`${itemClasses} ${itemClasses}--${locale} ${itemClasses}--chat`}
				>
					{renderChatElement(item, chatStatus, interactWithProactiveChat, true)}
				</li>
			);
		} else {
			return null;
		}
	};

	const renderItem = (item, index, chatStatus, statusKey) => {
		const href = mapString(item.link.href, window.location.pathname);
		const itemId = item?.id;

		if (href === `#${chatButtonId}`) {
			return (
				<Fragment key={`chatButtons-${index}`}>
					{renderChatButton(chatStatus, statusKey, item)}
					{renderProactiveChatButton(chatStatus, item)}
				</Fragment>
			);
		} else {
			if (
				isCurrentPathPresent(
					[`${href}*`, `${buildHref(href, store, "", false)}*`],
					currentPath
				)
			)
				return null;

			return (
				<li
					className={`${itemClasses} ${itemClasses}--${itemId}`}
					key={`${item.link.analyticsLinkId}-${index}`}
				>
					<Link
						className={`${mainClass}__popup__items__item__link ${mainClass}__popup__items__item__link--${itemId}`}
						style={{ flexDirection }}
						href={href}
						handleClick={(event) => handleClick({ event, item, href })}
					>
						<ItemContent item={item} />
					</Link>
				</li>
			);
		}
	};

	const handleClick = ({ event, item, href }) => {
		event.preventDefault();

		if (item?.id === "shareWebsiteFeedback") {
			const urlWithParams = getSurveyUrl(
				[href],
				profileData?.user?.swid,
				"Anonymous"
			);
			setFeedbackUrl(urlWithParams);
			setAnalyticsEvent("fhhFeedback");
			if (isMobileView) toggleBodyClass();
			if (ariaLiveRef?.current) {
				ariaLiveRef.current.textContent =
					response?.properties?.shareWebsiteFeedback;
			}
		} else {
			setAnalyticsEvent(`fhh#${href}`);
			setTimeout(() => {
				window.location.assign(
					event?.target?.href || event?.target?.parentNode?.href || href
				);
			}, 400);
		}
	};

	const closePopUpAndSendEvent = () => {
		handleClosePopup();
		setAnalyticsEvent("fhhClosed");
	};

	const renderItems = () => {
		let chatStatus = null;
		let statusKey = "";
		if (currentStates.isChatInvite) {
			chatStatus = response.chatWithUsStates?.invite;
		} else {
			statusKey = Array.from(chatFloatingButton?.classList || [])
				.find((className) => className.includes("status-"))
				?.split("status-")?.[1];
			if (statusKey) {
				chatStatus = response.chatWithUsStates[statusKey];
			}
		}
		let itemsToRender = response.userInteraction?.items || [];
		if (statusKey) {
			itemsToRender = [...itemsToRender].reverse();
		}
		return itemsToRender.map((item, index) =>
			renderItem(item, index, chatStatus, statusKey)
		);
	};

	useEffect(() => {
		setFloatingHelpHubUrl(content.apiUrl.floatingHelpHub);
	}, []);

	useEffect(() => {
		if (chatFloatingButton) {
			chatFloatingButton.addEventListener("click", handleClosePopup);
			removeLegacyChatButtonFocus();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatFloatingButton]);

	useEffect(() => {
		if (chatModal) {
			if (
				chatModal.classList?.contains("syndicated-floating-help-hub-invite")
			) {
				removeLegacyChatButtonFocus();
				setCurrentStates({
					isChatInvite: true,
					shouldCloseAfterChatInvite: isShowPopup ? false : true,
				});
				document.addEventListener("keydown", addFocusModalEvent);
				if (!isShowPopup) handleShowPopup(true);
				if (ariaLiveRef?.current)
					ariaLiveRef.current.textContent = response?.properties?.ariaLabel;
			}
		} else if (currentStates.isChatInvite) {
			if (currentStates.shouldCloseAfterChatInvite) {
				handleClosePopup();
			}
			setCurrentStates({
				isChatInvite: false,
				shouldCloseAfterChatInvite: false,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatModal]);

	useEffect(() => {
		if (response) {
			const isExcluded =
				response.excludedPages && isCurrentPathPresent(response.excludedPages);
			const isAllowed =
				response.allowedPages && isCurrentPathPresent(response.allowedPages);

			if (!isExcluded && isAllowed) {
				setIsEnabled(true);
			}
		}
	}, [response]);

	useEffect(() => {
		if (chatWindow) handleClosePopup();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatWindow]);

	useEffect(() => {
		if (feedbackUrl) {
			toggleBodyClass(isMobileView);
		}
	}, [isMobileView, feedbackUrl]);

	const dialogContent =
		currentStates.isChatInvite && response?.invite
			? response.invite
			: response?.userInteraction;

	return isEnabled && response && dialogContent?.properties ? (
		<section className={mainClass} style={{ visibility: "hidden" }}>
			<div
				className={`${mainClass}__container`}
				style={{
					display: isShowPopup ? "block" : "none",
					"--fhh-dialogBackgroundColor":
						response.properties.dialogBackgroundColor,
				}}
			>
				<figure
					className={`${mainClass}__overlay ${mainClass}__overlay__${animationState}`}
					onClick={closePopUpAndSendEvent}
				/>
				<div
					className={`${mainClass}__popup ${mainClass}__popup--${animationState}`}
					tabIndex="-1"
					role="dialog"
					aria-label={`${
						dialogContent.properties.image.alt || dialogContent.properties.alt
					} ${dialogContent.properties.title} - ${
						dialogContent.properties.subtitle
					}`}
				>
					<div
						className={`${mainClass}__popup__header`}
						style={{ display: !feedbackUrl ? "block" : "none" }}
					>
						<figure className={`${mainClass}__popup__header__circle`}>
							<img
								className={`${mainClass}__popup__header__circle__image`}
								src={dialogContent.properties.image?.url}
								alt={dialogContent.properties.alt}
								loading="lazy"
							/>
						</figure>
						<h3
							className={`${mainClass}__popup__header__title`}
							style={{
								textAlign:
									response.properties.dialogTitleTextAlign ||
									defaultProperties.titleAlign,
							}}
						>
							{dialogContent.properties.title}
						</h3>
						<h4
							className={`${mainClass}__popup__header__subtitle`}
							style={{
								textAlign:
									response.properties.dialogSubtitleAlign ||
									defaultProperties.subtitleAlign,
							}}
						>
							{dialogContent.properties.subtitle}
						</h4>
						<button
							className={`${mainClass}__popup__header__close-button`}
							aria-label="Close"
							onClick={closePopUpAndSendEvent}
							ref={dialogRef}
							tabIndex={0}
						>
							<Icon icon={"icon_close-reversed"} />
						</button>
					</div>
					<ul
						className={`${mainClass}__popup__items${
							currentStates.isChatInvite
								? ` ${mainClass}__popup__items--proactive`
								: ""
						}`}
						style={{
							...keysToCssProperties(),
							display: !feedbackUrl ? "flex" : "none",
						}}
					>
						{renderItems()}
					</ul>

					{feedbackUrl && (
						<div className={`${mainClass}__popup__iframe`}>
							<div className={`${mainClass}__popup__iframe__header`}>
								<h4 className={`${mainClass}__popup__iframe__header__title`}>
									{response?.properties?.shareWebsiteFeedback}
								</h4>
								<button
									className={`${mainClass}__popup__iframe__header__close-button`}
									aria-label="Close Form"
									onClick={closePopUpAndSendEvent}
									tabIndex={0}
								>
									<Icon icon={"icon_close-reversed"} />
								</button>
							</div>
							<iframe
								src={feedbackUrl}
								className={`${mainClass}__popup__iframe__body`}
								title={response?.properties?.shareWebsiteFeedback}
								frameBorder="0"
							/>
						</div>
					)}
				</div>
			</div>
			<button
				className={`${mainClass}__button`}
				aria-haspopup="dialog"
				onClick={() => {
					handleShowPopup();
					setAnalyticsEvent("fhhOpened");
				}}
				style={{
					"--fhh-floatingButtonBackground":
						response.properties.floatingButtonBackground,
					display: isShowPopup || chatWindow ? "none" : "block",
				}}
				tabIndex="0"
				ref={showButtonRef}
			>
				{(
					response.properties.floatingButtonLabelType ||
					defaultProperties.buttonLabelType
				)?.includes("icon") && (
					<Icon
						className={`${mainClass}__button__icon${
							response.properties.floatingButtonHideIconInMobile === "false"
								? ""
								: ` ${mainClass}__button__icon--hide-in-mobile`
						}`}
						icon={response.properties.icon}
					/>
				)}
				{(
					response.properties.floatingButtonLabelType ||
					defaultProperties.buttonLabelType
				)?.includes("title") && (
					<p className={`${mainClass}__button__text`}>
						{response.properties.title}
					</p>
				)}
			</button>
		</section>
	) : null;
};

export default FloatingHelpHub;
